import { PATH_KEYS } from "../services/appServices";
import { useItemService } from "./useServices";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { WorkspaceComment } from "@/models/workspace/WorkspaceComment";
import { useItemsCrud } from "./useItemsCrud";
import { restError } from "@/models/client/RestResponse";

const useWorkspaceComments = () => {
  /**** Services ****/
  const commentService = useItemService((services) => services.comment);

  const commentCrud = useItemsCrud<WorkspaceComment>(
    [PATH_KEYS.comment],
    (services) => services.comment
  );

  const queryClient = useQueryClient();

  /**** Queries & Mutations ****/
  const useQueryCommentsByFileId = (workspaceId: string, fileId: string) =>
    useQuery<WorkspaceComment[]>({
      queryKey: [PATH_KEYS.comment, workspaceId, fileId],
      queryFn: async () => {
        const response = await commentService.getCommentsByFileId(
          workspaceId,
          fileId
        );
        if (response.success) return response.value;
        else throw restError(response);
      },
    });

  const useQueryGeneralWorkspaceComments = (workspaceId: string) =>
    useQuery<WorkspaceComment[]>({
      queryKey: [PATH_KEYS.comment, "workspace", workspaceId, "general"],
      queryFn: async () => {
        const response =
          await commentService.getGeneralWorkspaceComments(workspaceId);
        if (response.success) return response.value;
        else throw restError(response);
      },
    });

  const updateCommentContent = useMutation<
    WorkspaceComment,
    string,
    { id: string; content: string; fileId?: string }
  >({
    mutationKey: [PATH_KEYS.comment, "updateContent"],
    mutationFn: async ({ id, content, fileId }) => {
      const response = await commentService.updateCommentContent(id, {
        content,
        fileId,
      });
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === PATH_KEYS.comment,
      });
    },
  });

  const updateCommentReaction = useMutation<
    WorkspaceComment,
    string,
    { id: string; reaction: string; fileId?: string }
  >({
    mutationKey: [PATH_KEYS.comment, "updateReaction"],
    mutationFn: async ({ id, reaction, fileId }) => {
      const response = await commentService.updateCommentReaction(id, {
        reaction,
        fileId,
      });
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === PATH_KEYS.comment,
      });
    },
  });

  const addCommentReply = useMutation<
    WorkspaceComment,
    string,
    {
      id: string;
      content: string;
      workspaceId: string;
      fileId?: string;
    }
  >({
    mutationKey: [PATH_KEYS.comment, "addReply"],
    mutationFn: async ({ id, content, workspaceId, fileId }) => {
      const response = await commentService.addCommentReply(id, {
        content,
        workspaceId,
        fileId,
      });
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === PATH_KEYS.comment,
      });
    },
  });

  const deleteComment = useMutation<boolean, string, { id: string }>({
    mutationKey: [PATH_KEYS.comment, "delete"],
    mutationFn: async ({ id }) => {
      const response = await commentService.deleteComment(id);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === PATH_KEYS.comment,
      });
    },
  });

  /**** Value ****/
  return {
    ...commentCrud,
    useQueryCommentsByFileId,
    useQueryGeneralWorkspaceComments,
    updateCommentContent,
    updateCommentReaction,
    addCommentReply,
    deleteComment,
  };
};

export default useWorkspaceComments;
